import React from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import EvStationRoundedIcon from "@mui/icons-material/EvStationRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import { CustomChart, ChartSeriesConfig, ChartGroupConfig, ChartValueAxisConfig } from "@/components/chart/CustomChart";
import {
  FONT_SIZE,
  FONT_WEIGHT,
  TEXT_COLOR,
  BACKGROUND_COLOR,
  COLOR_EV_LINE,
  COLOR_LPG_LINE,
  AXIS_LEFT,
} from "../GoFurtherStyles";
import { SectionGridItemBannerCard, SecondaryPanel } from "./GoFurtherPrimitives";
import { formatMileageAxisTick, type ComparisonDataItem } from "../GoFurtherUtils";
import { createGoFurtherTooltip } from "./GoFurtherChartTooltip";

interface GoFurtherSuccessCaseSectionProps {
  chartData: ComparisonDataItem[];
}

const comparisonSeries: ChartSeriesConfig[] = [
  { dataKey: "evMileage", name: "EV 走行距離", type: "line", color: COLOR_EV_LINE, yAxisId: AXIS_LEFT },
  { dataKey: "lpgMileage", name: "LPG 走行距離", type: "line", color: COLOR_LPG_LINE, yAxisId: AXIS_LEFT },
];

const comparisonGroups: ChartGroupConfig[] = [
  { title: "EV", dataKeys: ["evMileage"] },
  { title: "LPG", dataKeys: ["lpgMileage"] },
];

const comparisonAxes: ChartValueAxisConfig[] = [
  {
    id: AXIS_LEFT,
    orientation: "left",
    tickFormatter: formatMileageAxisTick,
  },
];

const SuccessCaseTooltip = createGoFurtherTooltip({
  getUnit: () => "km",
});

const GoFurtherSuccessCaseSection: React.FC<GoFurtherSuccessCaseSectionProps> = ({ chartData }) => (
  <SectionGridItemBannerCard
    banner="他社様の成功事例紹介"
    title={
      <Typography component="h2" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
        LPG同等を達成している事業者様の稼働推移と成功要因
      </Typography>
    }
    subtitle="貴社のデータではありません"
  >
    <Grid item xs={12}>
      <CustomChart
        title={
          <Typography sx={{ p: 2, fontSize: FONT_SIZE.headline6 }}>
            <Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: "inherit" }}>
              LPG同等を達成している事業者様
            </Typography>
            の稼働推移(月別・車両1台あたり)
          </Typography>
        }
        description={
          <>
            <Typography component="p" sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
              EV以外の走行距離が貴社データに近い複数営業所のデータを、平均して算出しております。同じ交通圏等のくくりではございませんが、EV以外の走行距離が近い事業者様同士の比較とすることで、EVがLPG同等に稼働できた人、参考事例であると考えております。
            </Typography>
            <Typography component="p" sx={{ mt: 1, fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
              ※走行距離は実績となります
            </Typography>
          </>
        }
        data={chartData}
        series={comparisonSeries}
        groups={comparisonGroups}
        CustomTooltip={SuccessCaseTooltip}
        categoryAxis={{ dataKey: "month" }}
        valueAxes={comparisonAxes}
        height={300}
      />
    </Grid>

    <Grid item xs={12}>
      <SecondaryPanel sx={{ p: 0, bgcolor: BACKGROUND_COLOR.WHITE, border: "none" }}>
        <Typography sx={{ fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.PRIMARY, textAlign: "center" }}>
          実際にLPG同等を達成している事業者様の成功要因・事例
        </Typography>

        <Box sx={{ mt: 4, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3 }}>
          {[
            {
              title: "成功要因1",
              icon: <EvStationRoundedIcon sx={{ mt: 2, color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4 }} />,
              label: (
                <>
                  <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>充電運用体制を</Box>
                  <br />
                  構築している
                </>
              ),
              examples: ["休憩時間中の\n外部充電器の積極的な活用", "営業所の急速充電器の\n分散利用(シフト調整)"],
            },
            {
              title: "成功要因2",
              icon: <BadgeRoundedIcon sx={{ mt: 2, color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4 }} />,
              label: (
                <>
                  <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>乗務員様への説明</Box>
                  ＆
                  <br />
                  <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>再配置</Box>
                  をしている
                </>
              ),
              examples: ["乗務員様への充電方法や\n推奨スポットの周知", "2人車運用など\nEV乗務員配置の最適化"],
            },
            {
              title: "成功要因3",
              icon: <AutorenewRoundedIcon sx={{ mt: 2, color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4 }} />,
              label: (
                <>
                  <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>取り組みを継続</Box>
                  <br />
                  している
                </>
              ),
              examples: ["一部車両から全車両への\n段階的拡大", "帰庫時の残充電ルールなどの\n定型業務化(マニュアル化)"],
            },
          ].map((item) => (
            <Box
              key={item.title}
              sx={{
                fontSize: FONT_SIZE.body1,
                bgcolor: BACKGROUND_COLOR.SECONDARY,
                borderRadius: 2,
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.BOLD }}>{item.title}</Typography>

              {item.icon}

              <Typography
                sx={{
                  mt: 2,
                  color: TEXT_COLOR.PRIMARY,
                  fontSize: FONT_SIZE.headline6,
                  fontWeight: FONT_WEIGHT.BOLD,
                  textAlign: "center",
                }}
              >
                {item.label}
              </Typography>

              <Divider sx={{ mt: 2, width: "100%" }} />

              <Box sx={{ mt: 2, px: 2, width: "100%", display: "grid", gridTemplateColumns: "2em 1fr", columnGap: 1, rowGap: 1 }}>
                {item.examples.map((example, i) => (
                  <React.Fragment key={i}>
                    <Typography sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption, textAlign: "right", alignSelf: "center" }}>事例</Typography>
                    <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.caption, whiteSpace: "pre-line" }}>
                      {example}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </SecondaryPanel>
    </Grid>
  </SectionGridItemBannerCard>
);

export default GoFurtherSuccessCaseSection;
